import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { Building2, MapPin, Hash, Receipt, ShieldX, Search } from 'lucide-react';
import { controleerKvk, foutMelding, OpleiderKvkData, OpleiderOnboardingState } from '../../services/opleiderOnboardingApi';
import { Kaart, StapKop, Veld, Knop, Melding } from './ui';

export const StapKvk: React.FC<{ state: OpleiderOnboardingState; onKlaar: () => void; onGeweigerd: () => void }> = ({ state, onKlaar, onGeweigerd }) => {
  const { t } = useTranslation('pages');
  const [form, setForm] = useState<OpleiderKvkData>({
    organisatie_naam: state.provider.organisatie_naam ?? '',
    postcode: state.provider.postcode ?? '',
    plaats: state.provider.stad ?? '',
    kvk_nummer: state.provider.kvk_nummer ?? '',
    btw_nummer: state.provider.btw_nummer ?? '',
  });
  const zet = (veld: keyof OpleiderKvkData) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [veld]: veld === 'kvk_nummer' ? e.target.value.replace(/\D/g, '').slice(0, 8) : e.target.value }));

  const controle = useMutation({
    mutationFn: () => controleerKvk(form),
    onSuccess: onKlaar,
    onError: (err) => {
      if ((err as { response?: { data?: { code?: string } } })?.response?.data?.code === 'SBI_GEWEIGERD') onGeweigerd();
    },
  });

  return (
    <Kaart>
      <StapKop
        nummer={3}
        titel={t('opleiderWizard.kvk.titel', { defaultValue: 'KvK-check & SBI-poortwacht' })}
        intro={t('opleiderWizard.kvk.intro', {
          defaultValue:
            'We controleren uw inschrijving realtime in het Handelsregister. Alleen instellingen met een SBI-activiteit in opleiding, onderwijs, psychologie, coaching of organisatieadvies worden toegelaten.',
        })}
      />
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          controle.mutate();
        }}
      >
        <Veld
          name="organisatie_naam"
          required
          label={t('opleiderWizard.kvk.naam', { defaultValue: 'Officiële bedrijfsnaam (KvK)' })}
          icoon={<Building2 className="h-4 w-4" />}
          value={form.organisatie_naam}
          onChange={zet('organisatie_naam')}
          autoComplete="organization"
        />
        <div className="grid gap-4 sm:grid-cols-[160px_1fr]">
          <Veld
            name="postcode"
            required
            label={t('opleiderWizard.kvk.postcode', { defaultValue: 'Postcode hoofdkantoor' })}
            icoon={<MapPin className="h-4 w-4" />}
            value={form.postcode}
            onChange={zet('postcode')}
            placeholder="1234 AB"
            autoComplete="postal-code"
          />
          <Veld
            name="plaats"
            required
            label={t('opleiderWizard.kvk.plaats', { defaultValue: 'Plaatsnaam' })}
            value={form.plaats}
            onChange={zet('plaats')}
            autoComplete="address-level2"
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Veld
            name="kvk_nummer"
            required
            inputMode="numeric"
            pattern="\d{8}"
            label={t('opleiderWizard.kvk.kvk', { defaultValue: '8-cijferig KvK-nummer' })}
            icoon={<Hash className="h-4 w-4" />}
            value={form.kvk_nummer}
            onChange={zet('kvk_nummer')}
            placeholder="12345678"
          />
          <Veld
            name="btw_nummer"
            required
            label={t('opleiderWizard.kvk.btw', { defaultValue: 'Btw-nummer' })}
            icoon={<Receipt className="h-4 w-4" />}
            value={form.btw_nummer}
            onChange={zet('btw_nummer')}
            placeholder="NL123456789B01"
          />
        </div>

        <details className="rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 text-sm text-slate-400">
          <summary className="cursor-pointer text-slate-300">{t('opleiderWizard.kvk.wittelijst', { defaultValue: 'Welke SBI-codes worden toegelaten?' })}</summary>
          <ul className="mt-2 space-y-1">
            {Object.entries(state.sbi_witte_lijst).map(([code, label]) => (
              <li key={code}>
                <span className="font-mono text-sky-300">{code}</span> — {label}
              </li>
            ))}
          </ul>
        </details>

        {controle.isError && (controle.error as { response?: { data?: { code?: string } } })?.response?.data?.code !== 'SBI_GEWEIGERD' && (
          <Melding>{foutMelding(controle.error, t('opleiderWizard.kvk.fout', { defaultValue: 'De KvK-controle is mislukt.' }))}</Melding>
        )}

        <div className="flex justify-end pt-2">
          <Knop type="submit" bezig={controle.isPending} disabled={form.kvk_nummer.length !== 8}>
            {!controle.isPending && <Search className="h-4 w-4" aria-hidden="true" />}
            {controle.isPending
              ? t('opleiderWizard.kvk.bezig', { defaultValue: 'Handelsregister raadplegen…' })
              : t('opleiderWizard.kvk.submit', { defaultValue: 'Controleer bij KvK' })}
          </Knop>
        </div>
      </form>
    </Kaart>
  );
};

/** Harde weigeringspagina: geen SBI-match met de witte lijst → onboarding afgebroken. */
export const SbiWeigering: React.FC<{ state: OpleiderOnboardingState; onUitloggen: () => void }> = ({ state, onUitloggen }) => {
  const { t } = useTranslation('pages');
  const codes = state.provider.kvk_sbi_codes ?? [];
  return (
    <Kaart className="border-rose-500/30 text-center">
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-500/10 ring-1 ring-rose-500/40">
        <ShieldX className="h-8 w-8 text-rose-400" aria-hidden="true" />
      </div>
      <h2 className="text-2xl font-bold text-white">{t('opleiderWizard.weigering.titel', { defaultValue: 'Aanmelding geweigerd' })}</h2>
      <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-slate-300">
        {t('opleiderWizard.weigering.tekst', {
          defaultValue:
            'De KvK-inschrijving van {{naam}} bevat geen SBI-activiteit die toegang geeft tot de BeLagom Academy. Het platform is uitsluitend toegankelijk voor erkende opleidings-, onderwijs-, psychologie-, coaching- en organisatieadviesbureaus.',
          naam: state.provider.kvk_handelsnaam || state.provider.organisatie_naam || 'uw onderneming',
        })}
      </p>
      <div className="mx-auto mt-6 grid max-w-lg gap-4 text-left sm:grid-cols-2">
        <div className="rounded-xl border border-white/10 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('opleiderWizard.weigering.uwCodes', { defaultValue: 'Uw SBI-codes' })}</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-300">
            {codes.length === 0 && <li>—</li>}
            {codes.map((c) => (
              <li key={c.code}>
                <span className="font-mono text-rose-300">{c.code}</span> {c.omschrijving}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-xl border border-white/10 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t('opleiderWizard.weigering.toegestaan', { defaultValue: 'Toegestaan' })}</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-300">
            {Object.entries(state.sbi_witte_lijst).map(([code, label]) => (
              <li key={code}>
                <span className="font-mono text-emerald-300">{code}</span> {label}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <p className="mx-auto mt-6 max-w-lg text-xs text-slate-500">
        {t('opleiderWizard.weigering.bezwaar', {
          defaultValue: 'Denkt u dat dit onjuist is, bijvoorbeeld door een recente wijziging in het Handelsregister? Neem contact op met support@belagom.nl met uw KvK-nummer.',
        })}
      </p>
      <div className="mt-6">
        <Knop variant="secundair" onClick={onUitloggen}>
          {t('opleiderWizard.weigering.uitloggen', { defaultValue: 'Uitloggen' })}
        </Knop>
      </div>
    </Kaart>
  );
};
